import React, { useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { parseCsv } from "@/lib/csv";
import { cn } from "@/lib/utils";

export interface ImportField {
  key: string;
  label: string;
  required?: boolean;
  /** Other names the column may have in a file */
  aliases?: string[];
  /** A problem with this value, or null */
  check?: (value: string) => string | null;
}

type Row = Record<string, string>;
const norm = (s: string) => s.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
const MAX_ROWS = 300;

/**
 * Import records from a CSV in three steps: choose the file, check how its columns line up with the fields (and fix the mapping),
 * then see a preview with every row that has a problem before anything is added. Each valid row is created with `create`;
 * rows that fail on the server are listed afterwards.
 */
export function CsvImportDialog({
  open,
  onClose,
  title,
  noun,
  fields,
  create,
  onDone,
  hint,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  noun: string;
  fields: ImportField[];
  create: (row: Row) => Promise<void>;
  onDone: () => void;
  hint?: string;
}) {
  const [header, setHeader] = useState<string[]>([]);
  const [data, setData] = useState<string[][]>([]);
  const [map, setMap] = useState<Record<string, number>>({});
  const [fileName, setFileName] = useState("");
  const [problem, setProblem] = useState("");
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<{ ok: number; errors: { row: number; reason: string }[] } | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const reset = () => {
    setHeader([]);
    setData([]);
    setMap({});
    setFileName("");
    setProblem("");
    setResult(null);
    setProgress(0);
    if (input.current) input.current.value = "";
  };
  const close = () => {
    if (running) return;
    reset();
    onClose();
  };

  const load = async (file?: File | null) => {
    reset();
    if (!file) return;
    try {
      const rows = parseCsv(await file.text()).filter((r) => r.some((c) => c.trim() !== ""));
      if (rows.length < 2) throw new Error("The file has no rows");
      if (rows.length - 1 > MAX_ROWS) throw new Error(`Import at most ${MAX_ROWS} ${noun} at a time`);
      const head = rows[0]!.map((h) => h.trim());
      const auto: Record<string, number> = {};
      for (const f of fields) {
        const names = [f.key, f.label, ...(f.aliases || [])].map(norm);
        const i = head.findIndex((h) => names.includes(norm(h)));
        if (i >= 0) auto[f.key] = i;
      }
      setFileName(file.name);
      setHeader(head);
      setData(rows.slice(1));
      setMap(auto);
    } catch (e: any) {
      setProblem(e?.message || "Couldn't read that file");
    }
  };

  const rows: Row[] = useMemo(() => data.map((r) => Object.fromEntries(fields.map((f) => [f.key, map[f.key] !== undefined ? String(r[map[f.key]!] ?? "").trim() : ""]))), [data, map, fields]);
  const issues = useMemo(
    () =>
      rows.map((r) => {
        for (const f of fields) {
          if (f.required && !r[f.key]) return `${f.label} is missing`;
          if (r[f.key] && f.check) {
            const p = f.check(r[f.key]!);
            if (p) return p;
          }
        }
        return null;
      }),
    [rows, fields],
  );
  const missingRequired = fields.filter((f) => f.required && map[f.key] === undefined);
  const ready = issues.filter((i) => !i).length;

  const run = async () => {
    setRunning(true);
    setProgress(0);
    const errors: { row: number; reason: string }[] = [];
    let ok = 0;
    for (let i = 0; i < rows.length; i++) {
      if (issues[i]) {
        errors.push({ row: i + 2, reason: issues[i]! });
      } else {
        try {
          await create(rows[i]!);
          ok++;
        } catch (e: any) {
          errors.push({ row: i + 2, reason: e?.message || "Could not add" });
        }
      }
      setProgress(i + 1);
    }
    setRunning(false);
    setResult({ ok, errors });
    if (ok) onDone();
  };

  const showPreview = data.length > 0 && !result;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {hint || `Choose a CSV with one ${noun.replace(/s$/, "")} per row and a header row.`} Nothing is added until you have seen the preview and pressed Import.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1">
          <input ref={input} type="file" accept=".csv,text/csv" disabled={running} onChange={(e) => void load(e.target.files?.[0])} className="text-sm" />
          {problem && <p className="text-xs text-destructive">{problem}</p>}
        </div>

        {showPreview && (
          <>
            <section>
              <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">1. Match the columns of {fileName}</h3>
              <div className="grid gap-x-4 gap-y-1.5 sm:grid-cols-2">
                {fields.map((f) => (
                  <label key={f.key} className="flex items-center justify-between gap-2 text-sm">
                    <span className={cn(f.required && "font-medium")}>
                      {f.label}
                      {f.required && <span className="text-destructive"> *</span>}
                    </span>
                    <select
                      value={map[f.key] ?? ""}
                      onChange={(e) => setMap((m) => (e.target.value === "" ? Object.fromEntries(Object.entries(m).filter(([k]) => k !== f.key)) : { ...m, [f.key]: Number(e.target.value) }))}
                      className="h-8 w-44 rounded-md border border-input bg-background px-2 text-xs"
                    >
                      <option value="">— not in the file —</option>
                      {header.map((h, i) => (
                        <option key={i} value={i}>
                          {h || `Column ${i + 1}`}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
              {missingRequired.length > 0 && <p className="mt-1.5 text-xs text-destructive">Match {missingRequired.map((f) => f.label).join(", ")} to a column to continue.</p>}
            </section>

            <section>
              <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">2. Preview</h3>
              <p className="mb-1.5 text-sm">
                <strong>{ready}</strong> of {rows.length} {rows.length === 1 ? noun.replace(/s$/, "") : noun} ready to add{rows.length - ready ? `, ${rows.length - ready} with a problem (skipped)` : ""}.
              </p>
              <div className="max-h-56 overflow-auto rounded-md border border-border">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-panel">
                    <tr>
                      <th className="px-2 py-1 text-left font-medium text-muted-foreground">Row</th>
                      {fields.filter((f) => map[f.key] !== undefined).map((f) => (
                        <th key={f.key} className="px-2 py-1 text-left font-medium text-muted-foreground">
                          {f.label}
                        </th>
                      ))}
                      <th className="px-2 py-1 text-left font-medium text-muted-foreground">Check</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 50).map((r, i) => (
                      <tr key={i} className={cn("border-t border-border/60", issues[i] && "bg-danger-soft")}>
                        <td className="px-2 py-1 text-muted-foreground">{i + 2}</td>
                        {fields.filter((f) => map[f.key] !== undefined).map((f) => (
                          <td key={f.key} className="max-w-[160px] truncate px-2 py-1">
                            {r[f.key] || <span className="text-muted-foreground">—</span>}
                          </td>
                        ))}
                        <td className={cn("px-2 py-1", issues[i] ? "text-danger" : "text-success")}>{issues[i] || "OK"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length > 50 && <p className="mt-1 text-xs text-muted-foreground">Showing the first 50 rows; all {rows.length} are checked.</p>}
            </section>
          </>
        )}

        {running && (
          <p className="text-sm text-muted-foreground">
            Adding… {progress} of {rows.length}
          </p>
        )}
        {result && (
          <div className="space-y-1.5 text-sm">
            <p>
              <strong>{result.ok}</strong> {result.ok === 1 ? noun.replace(/s$/, "") : noun} added{result.errors.length ? `, ${result.errors.length} skipped` : ""}.
            </p>
            {result.errors.slice(0, 12).map((er) => (
              <p key={er.row} className="text-xs text-danger">
                Row {er.row}: {er.reason}
              </p>
            ))}
            {result.errors.length > 12 && <p className="text-xs text-muted-foreground">…and {result.errors.length - 12} more.</p>}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={running}>
            {result ? "Close" : "Cancel"}
          </Button>
          {showPreview && (
            <Button disabled={running || missingRequired.length > 0 || ready === 0} onClick={run}>
              {running ? "Importing…" : `Import ${ready} ${ready === 1 ? noun.replace(/s$/, "") : noun}`}
            </Button>
          )}
          {result && (
            <Button variant="outline" onClick={reset}>
              Import another file
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
